import React, { Fragment, memo, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Dialog, Transition } from '@headlessui/react';
import { oneLine } from 'common-tags';
import aa from 'search-insights';

import { Cart, Reload, Search, User, UserActive } from '@/icons';
import { CustomSkeletonTheme, NextLink } from '@/atoms';
import { Button } from '@/molecules';
import { ToastCenter } from '@/organisms';
import CartLines from '@/organisms/cart-lines';

import { useCart, useRouteChangeStart } from '@/lib/hooks';
import { routes } from '@/lib/routes';
import {
  SumCurrency,
  gtmPush,
  normalizeCartItems,
  priceToNumber,
  toCurrency,
} from '@/lib/utils';

import type { FunctionComponent } from 'react';
import type { ToolbarProps } from './types';

const EmptyCart = dynamic(() => import('@/assets/images/EmptyCart.svg'), {
  ssr: false,
});

const SearchBoxMobileInstantSearch = dynamic(
  () => import('@/components/algolia/search-box-mobile-instant-search'),
  { ssr: false },
);

const CartIcon = ({
  cartCircleState,
  cartNumberState,
  cart,
  loading,
}: {
  cartCircleState: TypeCartCicleState;
  cartNumberState: TypeCartNumberState;
  cart: any;
  loading: boolean;
}) => (
  <>
    <span
      className={oneLine`
        absolute right-0 -ml-2 
        flex h-6 w-6
         scale-0 items-center justify-center overflow-hidden rounded-full border-4 border-secondary-500 bg-secondary-500 text-center 
         font-mono text-sm font-medium text-white transition-transform 
         ${cartCircleState === 'show' ? 'animate-scale' : ''}
         ${
           cartCircleState === 'hidden'
             ? 'animate-[scaleAndBounce_.4s_linear_reverse]'
             : ''
         }
      `}
    >
      <span
        className={oneLine`
          -translate-y-[15px] transition-transform
          ${cartNumberState === 'show' ? 'animate-number-drop' : ''}
        `}
      >
        {!loading ? (
          cart?.contents?.itemCount
        ) : (
          <Reload width={15} className="animate-spin motion-reduce:hidden" />
        )}
      </span>
    </span>

    <span className="sr-only">
      {!loading ? cart?.contents?.itemCount : 'loading'} items in cart, view bag
    </span>
  </>
);

// eslint-disable-next-line react-hooks/exhaustive-deps
const CartIconMemo = memo(CartIcon);

type TypeCartCicleState = 'hidden' | 'show' | 'none';
type TypeCartNumberState = 'hidden' | 'show';

/**
 * Primary UI component for user interaction
 */
const ToolbarWithOutMemoried: FunctionComponent<ToolbarProps> = ({
  setLoginModalOpen,
  setMenuOpen,
}: ToolbarProps) => {
  const { cart, loading } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const { status, data } = useSession();

  // Optional: set the analytics user ID
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  aa('setUserToken', data?.user?.UID);
  const router = useRouter();
  const [searching, setSearching] = useState(false);
  const [lastCartQuantity, setLastCartQuantity] = useState(0);
  const [cartCircleState, setCartCircleState] =
    useState<TypeCartCicleState>('none');
  const [cartNumberState, setCartNumberState] =
    useState<TypeCartNumberState>('hidden');
  const close = () => setSearching(false);

  useEffect(() => {
    if (!loading) {
      if (lastCartQuantity === 0 && cart.contents.itemCount > 0) {
        // Aparece el circulo y cae el numero

        setCartCircleState('none');

        setTimeout(() => {
          setCartCircleState('show');
          setTimeout(() => {
            setCartNumberState('hidden');

            setTimeout(() => {
              setCartNumberState('show');
            }, 100);
          }, 500);
        }, 1);
      } else if (lastCartQuantity > 0 && cart.contents.itemCount > 0) {
        // Solo cae el numero

        setCartNumberState('hidden');

        setTimeout(() => {
          setCartNumberState('show');
        }, 100);
      } else if (lastCartQuantity > 0 && cart.contents.itemCount === 0) {
        // Desaparece el circulo

        setCartCircleState('none');

        setTimeout(() => {
          setCartCircleState('hidden');
          setTimeout(() => {
            setCartNumberState('hidden');
            setCartCircleState('none');
          }, 500);
        });
      }

      setLastCartQuantity(cart.contents.itemCount);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, loading]);

  useRouteChangeStart(close);

  return (
    <CustomSkeletonTheme>
      <div className="lg:justify-[normal] flex h-full items-center justify-end">
        {/* Search */}
        <div className="flex lg:hidden lg:items-center">
          <div onClick={() => setSearching(true)} role="button">
            <span className="sr-only">Search</span>
            <Search className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>

        {/* User */}
        <div
          role="button"
          className="ml-6 flex gap-1 lg:ml-0 lg:items-center"
          style={{ zIndex: 4 }}
          onClick={() => {
            if (status === 'authenticated') {
              router.push(routes.micuenta.index);
            } else {
              setMenuOpen(-1);
              setLoginModalOpen(true);
            }
          }}
          data-playwright="openLoginModal"
        >
          <p className="u-body u-body--s hidden font-bold text-primary-900 lg:block">
            {status === 'authenticated' ? data?.user?.name : 'Inicia sesión'}
          </p>
          <span className="sr-only">Search</span>
          {status === 'authenticated' ? (
            <UserActive width={20} height={20} aria-hidden="true" />
          ) : (
            <User width={20} height={20} aria-hidden="true" />
          )}
        </div>

        {/* Cart Desktop */}
        <div
          className="relative ml-6 hidden lg:flex lg:items-center"
          onMouseEnter={() => setCartOpen(true)}
          onMouseLeave={() => setCartOpen(false)}
        >
          <div className="relative flex items-center pr-4">
            <Cart className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <CartIconMemo
              cartCircleState={cartCircleState}
              cartNumberState={cartNumberState}
              cart={cart}
              loading={loading}
            />

            <div
              className={oneLine`
                absolute -right-5 top-0 flex w-[370px] flex-col items-center justify-center pt-10
                ${!cartOpen ? 'hidden' : ''}
              `}
            >
              <div className="absolute right-[35px] top-[30px] z-0 h-5 w-5 rotate-45 border border-primary-100 bg-white shadow-lg" />
              <div className="relative z-10 h-full w-full border border-primary-100 bg-white p-6 shadow-lg">
                <div className="absolute -top-px right-[31px] h-px w-[26px] bg-white" />

                {/* <p className="u-headline u-headline--h3 mb-4 mt-2 w-full text-center">
                  tu cesta
                </p> */}

                <NextLink href={routes.cart} passHref>
                  <Button
                    label="IR A CESTA"
                    size="large-full"
                    className="mb-2"
                    as="a"
                    onClick={() => {
                      setCartOpen(false);
                    }}
                  />
                </NextLink>

                {!loading && cart.contents.itemCount === 0 ? (
                  <>
                    <p className="u-body u-body--s text-center normal-case text-primary-600">
                      Tu cesta está vacía. Echa un ojo a nuestra web. ¡Hay
                      productos muy interesantes!
                    </p>
                    <EmptyCart
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      className="my-6 mt-4 opacity-40"
                    />
                    <NextLink href={routes.cart} passHref>
                      <Button
                        label="IR A CESTA"
                        size="large-full"
                        className="mb-2"
                        as="a"
                        onClick={() => {
                          setCartOpen(false);
                        }}
                      />
                    </NextLink>
                  </>
                ) : (
                  <>
                    <CartLines cart={cart.contents.cart} loading={loading} />

                    <div className="u-body u-body--m flex w-full items-center justify-between pt-4">
                      <p>Envío</p>
                      <p>
                        {loading ? (
                          <Skeleton className="block min-w-[40px]" />
                        ) : (
                          SumCurrency(cart.shippingTotal, cart.shippingTax)
                        )}
                      </p>
                    </div>

                    <div className="flex w-full items-center justify-between pb-5 pt-3">
                      <p className="u-body u-body--m">
                        Total
                        <span className="u-body--xs pl-2 text-typo-tertiary">
                          (IVA incluido)
                        </span>
                      </p>
                      <p className="u-headline u-headline@mobile--h2">
                        {loading ? (
                          <Skeleton count={1} className="block min-w-[40px]" />
                        ) : (
                          toCurrency(cart.total)
                        )}
                      </p>
                    </div>

                    <NextLink href={routes.cart} passHref>
                      <Button
                        label="IR A CESTA"
                        size="large-full"
                        className="mb-2"
                        as="a"
                        onClick={() => {
                          setCartOpen(false);
                        }}
                      />
                    </NextLink>

                    <NextLink href={routes.checkout.datos} passHref>
                      <Button
                        label="Tramitar pedido"
                        size="large-full"
                        style="secondary"
                        as="a"
                        onClick={() => {
                          setCartOpen(false);
                          gtmPush({
                            event: 'begin_checkout',
                            ecommerce: {
                              discount: priceToNumber(
                                SumCurrency(
                                  cart.discountTotal,
                                  cart.discountTax,
                                ),
                              ),
                              coupon: cart.appliedCoupons,
                              items: normalizeCartItems(cart),
                            },
                          });
                        }}
                      />
                    </NextLink>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cart Mobile */}
        <div className="ml-6 lg:hidden">
          <NextLink href={routes.cart}>
            <a className="relative flex items-center pr-4">
              <Cart className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <CartIconMemo
                cartCircleState={cartCircleState}
                cartNumberState={cartNumberState}
                cart={cart}
                loading={loading}
              />
            </a>
          </NextLink>
        </div>
      </div>

      <Transition
        show={searching}
        as={Fragment}
        unmount={false}
        enter="transition ease-in-out duration-300 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition ease-in-out duration-300 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <Dialog
          unmount={false}
          as="div"
          className="fixed inset-0 z-40 flex lg:hidden"
          onClose={close}
          open={searching}
        >
          <div className="flex h-full w-full flex-col items-end bg-bg-secondary">
            <ToastCenter
              className="lg:hidden"
              style={{
                position: 'absolute',
                zIndex: 40,
                width: '100%',
                inset: 'auto',
              }}
            />

            {searching ? (
              <SearchBoxMobileInstantSearch
                placeholder="¿Qué estás buscando?"
                onClose={close}
              />
            ) : null}
          </div>
        </Dialog>
      </Transition>
    </CustomSkeletonTheme>
  );
};

export const Toolbar = memo(ToolbarWithOutMemoried);
